/**
 * Timezone utilities for display formatting
 */

/** Get the browser's IANA timezone string, e.g. "America/New_York" */
export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/New_York";
  }
};

/** Get short timezone abbreviation like "EST", "PST" */
export const getTimezoneAbbr = (tz) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz || getBrowserTimezone(),
      timeZoneName: "short",
    }).formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart ? tzPart.value : "";
  } catch {
    return "";
  }
};

/**
 * Convert a UTC "HH:MM" time (as stored in DB) to a human-readable local time string.
 * e.g. "14:00" UTC + date "2026-09-01" → "9:00 AM EDT" in America/New_York
 * @param {string} utcTime  - "HH:MM" stored in UTC
 * @param {string} [dateStr] - "YYYY-MM-DD" for DST-accurate conversion (defaults to today)
 */
export const utcTimeToLocal = (utcTime, dateStr) => {
  if (!utcTime) return "";
  try {
    const d = dateStr || new Date().toISOString().slice(0, 10);
    const utcDate = new Date(`${d}T${utcTime}:00Z`); // Z = UTC
    return utcDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  } catch {
    return utcTime;
  }
};

/**
 * Convert a UTC "HH:MM" string to a local "HH:MM" string suitable for <input type="time">.
 * e.g. "14:00" UTC → "09:00" in America/New_York (UTC-5)
 * @param {string} utcTime  - "HH:MM" in UTC
 * @param {string} [dateStr] - "YYYY-MM-DD" for DST accuracy
 */
export const utcTimeToLocalInput = (utcTime, dateStr) => {
  if (!utcTime) return utcTime || "";
  try {
    const d = dateStr || new Date().toISOString().slice(0, 10);
    const utcDate = new Date(`${d}T${utcTime}:00Z`);
    const hh = String(utcDate.getHours()).padStart(2, "0");
    const mm = String(utcDate.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch {
    return utcTime;
  }
};

/**
 * Convert a local "HH:MM" string + date to a UTC "HH:MM" string for DB storage.
 * e.g. "09:00" + "2026-09-01" in America/New_York → "14:00" UTC
 * @param {string} localTime - "HH:MM" as entered by the user in their browser's local time
 * @param {string} dateStr   - "YYYY-MM-DD" in local time (required for DST accuracy)
 */
export const localTimeToUtc = (localTime, dateStr) => {
  if (!localTime || !dateStr) return localTime || "";
  try {
    // No "Z" = the browser interprets this as local time
    const localDate = new Date(`${dateStr}T${localTime}:00`);
    const hh = String(localDate.getUTCHours()).padStart(2, "0");
    const mm = String(localDate.getUTCMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch {
    return localTime;
  }
};

/**
 * @deprecated Use utcTimeToLocal() instead.
 * Format a time string like "14:00" to "2:00 PM EST"
 */
export const formatTime12WithTz = (timeStr, tzAbbr) => {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h);
  const period = hour >= 12 ? "PM" : "AM";
  const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  const base = `${display}:${m} ${period}`;
  return tzAbbr ? `${base} ${tzAbbr}` : base;
};
